// src/pages/doctors/patients/DoctorPatientEhr.jsx
import React from "react";
import { ehrStatusFromServer } from "@/services/confirmedEhrRequest.mjs";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import TopBarCenter from "@/sot/primitives/TopBarCenter.jsx";
import AvatarThumb68 from "@/sot/primitives/AvatarThumb68.jsx";
import Divider from "@/components/ui/Divider.jsx";
import ListRow from "@/sot/primitives/ListRow.jsx";
import { supabase } from "@/services/supabaseClient";
import { loadAppointmentEhrAccessState } from "@/services/ehrAccessService.js";
import {
  fetchLatestPermittedPatientVitalSigns,
  formatCanonicalVitals,
} from "@/services/ehrVitalSignsService.js";
import { loadDoctorAppointmentSnapshot } from "@/pages/doctors/appointments/lib/doctorAppointmentsData.js";
import { useLanguage } from "@/contexts/LanguageContext.jsx";
import { normalizeConditionList } from "@/utils/conditions.js";

// DEV-only bypass so you can build without auth blocking you
// DEV-only bypass so you can build without auth blocking you
// Works on LAN/mobile too (when DEV is false but you still want bypass)
const DEV_BYPASS_EHR =
  import.meta?.env?.DEV === true ||
  import.meta?.env?.VITE_BYPASS_AUTH === "true" ||
  (typeof window !== "undefined" && window.localStorage?.getItem("afrimed:bypass_ehr") === "true");

async function resolvePatientRouteRefs(patientRef) {
  const candidate = String(patientRef || "").trim();
  if (!candidate) return [];

  const ids = new Set([candidate]);

  const [{ data: byUser }, { data: byRow }] = await Promise.all([
    supabase.from("patients").select("id").eq("user_id", candidate).maybeSingle(),
    supabase.from("patients").select("id").eq("id", candidate).maybeSingle(),
  ]);

  if (byUser?.id) ids.add(String(byUser.id));
  if (byRow?.id) ids.add(String(byRow.id));

  return Array.from(ids).filter(Boolean);
}

function calcAge(dobISO) {
  if (!dobISO) return null;
  const d = new Date(dobISO);
  if (Number.isNaN(d.getTime())) return null;
  const now = new Date();
  let age = now.getFullYear() - d.getFullYear();
  const m = now.getMonth() - d.getMonth();
  if (m < 0 || (m === 0 && now.getDate() < d.getDate())) age--;
  return age >= 0 && age <= 120 ? age : null;
}

const ICONS = {
  back: "/icons/doctor/appointments/arrow_back.svg",
  next: "/icons/doctor/appointments/navigate_next.svg",

  dob: "/icons/doctor/appointments/EHR/dob.svg",
  language: "/icons/doctor/appointments/EHR/language.svg",
  heightWeight: "/icons/doctor/appointments/EHR/height_weight.svg",
  bloodtype: "/icons/doctor/appointments/EHR/bloodtype.svg",
  medications: "/icons/doctor/appointments/EHR/medications.svg",
  allergy: "/icons/doctor/appointments/EHR/allergy.svg",
  conditions: "/icons/doctor/appointments/EHR/conditions.svg",
  organDonor: "/icons/doctor/appointments/EHR/organ_donor.svg",
  pastAppointments: "/icons/doctor/appointments/EHR/past_appointments.svg",

  accessRequested: "/icons/doctor/appointments/access_requested.svg",
  pending: "/icons/doctor/appointments/Pending.svg",
  ehr: "/icons/doctor/appointments/EHR.svg",
  verified: "/icons/patient/verified_blue.svg",
  pendingVerification: "/icons/pending-verification.svg",
};

function splitDisplayList(value) {
  if (Array.isArray(value)) {
    return value.flatMap((item) => splitDisplayList(item));
  }

  const text = String(value || "").trim();
  if (!text || text === "—") return [];

  return text
    .split(/[,;\n]+/)
    .flatMap((part) => String(part || "").split(/(?<=[a-z])(?=[A-Z][a-z])/g))
    .map((part) => String(part || "").replace(/\s+/g, " ").trim())
    .filter(Boolean);
}

function uniqueDisplayList(values = []) {
  const seen = new Set();
  const out = [];

  values.forEach((value) => {
    const key = String(value || "").trim().toLowerCase();
    if (!key || seen.has(key)) return;
    seen.add(key);
    out.push(value);
  });

  return out;
}

function formatLanguageDisplay(value, fallback = "—") {
  const items = uniqueDisplayList(splitDisplayList(value));
  return items.length ? items.join(", ") : fallback;
}

function formatConditionDisplay(value, fallback = "—") {
  const items = normalizeConditionList(splitDisplayList(value));
  return items.length ? items.join(", ") : fallback;
}

function formatEhrDateTime(value = "") {
  const date = value ? new Date(value) : null;
  if (!date || Number.isNaN(date.getTime())) return "";
  try {
    return new Intl.DateTimeFormat(undefined, {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    }).format(date);
  } catch {
    return String(value || "");
  }
}

function normalizeVitalRepEhrDocument(report = {}, appointmentById = new Map()) {
  const content = report?.content && typeof report.content === "object" ? report.content : {};
  const document = content.ehrClinicalDocument && typeof content.ehrClinicalDocument === "object"
    ? content.ehrClinicalDocument
    : {};
  const soap = document.structuredSoap || content.soapDraft || {};
  const appointmentId = String(document.appointmentId || report?.consultation?.appointment_id || "").trim();
  const appointment = appointmentById.get(appointmentId) || {};
  return {
    id: String(report?.id || "").trim(),
    appointmentId,
    title: String(report?.title || "VitalRep™ consultation note").trim(),
    status: String(content.status || document.status || "").trim(),
    finalizedAt: String(content.finalizedAt || document.finalizedAt || report?.updated_at || "").trim(),
    amendedAt: String(content.amendedAt || document.amendedAt || "").trim(),
    appointmentDate: String(appointment.appointment_date || "").trim(),
    subjective: String(soap.subjective || "").trim(),
    objective: String(soap.objective || "").trim(),
    assessment: String(soap.assessment || "").trim(),
    plan: String(soap.plan || "").trim(),
  };
}

export default function DoctorPatientEhr() {
  const nav = useNavigate();
  const { patientId: patientIdParam } = useParams(); // ✅ avoid name clash
  const location = useLocation();
  const { t, languageCode } = useLanguage();

  const state = location.state || {};
  const appointmentIdFromQuery = React.useMemo(() => {
    const params = new URLSearchParams(location.search || "");
    return String(params.get("appointmentId") || "").trim();
  }, [location.search]);

  // --- DEV-friendly param resolver: sometimes the URL param is actually an appointmentId ---
  const routeId = String(patientIdParam || "");
  const draftLookupId = appointmentIdFromQuery || routeId;

  // Try to read a draft keyed by appointment id (works even when we have no router state)
  let draftByAppt = null;
  try {
    const raw = sessionStorage.getItem(`afrimed:doctor_appt:${draftLookupId}`);
    draftByAppt = raw ? JSON.parse(raw) : null;
  } catch {
    draftByAppt = null;
  }
  if (!draftByAppt) {
    try {
      const raw = localStorage.getItem(`afrimed:doctor_appt:${draftLookupId}`);
      draftByAppt = raw ? JSON.parse(raw) : null;
    } catch {
      draftByAppt = null;
    }
  }

  // Base restored coming from state (common patterns in your repo)
  const stateHasPayload =
    !!state && typeof state === "object" && Object.keys(state).length > 0;
  const restoredFromState = state?.restored || (stateHasPayload ? state : null);

  // Prefer router/restored, but fall back to the appointment-keyed draft
  const restoredResolved = restoredFromState || draftByAppt || null;

  // If the URL param is actually an appointment UUID, treat it as appointmentId for nav
  const appointmentIdResolved =
    state?.appointmentId ||
    restoredResolved?.appointmentId ||
    restoredResolved?.id ||
    appointmentIdFromQuery ||
    routeId ||
    "";

  // Resolve patientId from whatever we have
  const patientIdResolved =
    restoredResolved?.patientId ||
    restoredResolved?.patient_id ||
    restoredResolved?.patient?.id ||
    restoredResolved?.patientProfile?.id ||
    (appointmentIdFromQuery ? routeId : null) ||
    null;

  // ✅ Back-compat aliases for the rest of the file (NO clashes now)
  const restored = restoredResolved;
  const appointmentId = appointmentIdResolved;
const patientId = patientIdResolved;
const [liveSnapshot, setLiveSnapshot] = React.useState(null);
const [liveClinicalProfile, setLiveClinicalProfile] = React.useState(null);
const [latestCanonicalVitals, setLatestCanonicalVitals] = React.useState(null);
const [vitalRepClinicalDocuments, setVitalRepClinicalDocuments] = React.useState([]);

const [ehrAccessStatus, setEhrAccessStatus] = React.useState("");
const [pastAppointmentsCountLive, setPastAppointmentsCountLive] = React.useState(null);

React.useEffect(() => {
  let alive = true;

  async function hydrateLiveSnapshot() {
    const apptId = String(appointmentIdResolved || "").trim();
    if (!apptId) {
      if (alive) setLiveSnapshot(null);
      return;
    }

    try {
      const { data: authData, error: authError } = await supabase.auth.getUser();
      const doctorAuthId = String(authData?.user?.id || "").trim();
      if (authError || !doctorAuthId) {
        if (alive) setLiveSnapshot(null);
        return;
      }

      const nextSnapshot = await loadDoctorAppointmentSnapshot({
        appointmentId: apptId,
        doctorId: doctorAuthId,
        supabase,
      });

      if (alive) {
        setLiveSnapshot(nextSnapshot || null);
      }
    } catch {
      if (alive) {
        setLiveSnapshot(null);
      }
    }
  }

  hydrateLiveSnapshot();
  window.addEventListener("afrimed:appointments:changed", hydrateLiveSnapshot);
  return () => {
    alive = false;
    window.removeEventListener("afrimed:appointments:changed", hydrateLiveSnapshot);
  };
}, [appointmentIdResolved]);

React.useEffect(() => {
  let alive = true;

  async function hydrateClinicalProfile() {
    const patientRef = String(
      patientIdResolved ||
        liveSnapshot?.patient_row_id ||
        liveSnapshot?.patient?.patient_id ||
        liveSnapshot?.patient?.id ||
        ""
    ).trim();

    if (!patientRef) {
      if (alive) setLiveClinicalProfile(null);
      return;
    }

    try {
      const patientIds = await resolvePatientRouteRefs(patientRef);
      const patientUserRows = patientIds.length
        ? await supabase.from("patients").select("id,user_id").in("id", patientIds)
        : { data: [] };
      const patientUserIds = Array.from(
        new Set(
          (Array.isArray(patientUserRows?.data) ? patientUserRows.data : [])
            .map((row) => String(row?.user_id || "").trim())
            .filter(Boolean)
        )
      );

      const [patientProfilesRes, ehrInfoRes] = await Promise.all([
        patientUserIds.length
          ? supabase.from("patient_profiles").select("*").in("user_id", patientUserIds)
          : Promise.resolve({ data: [] }),
        patientUserIds.length
          ? supabase
              .from("ehr_patient_info")
              .select("patient_id,date_of_birth,primary_language,height_cm,weight_kg,blood_type,current_medications,allergies,chronic_conditions,is_organ_donor,created_at,updated_at")
              .in("patient_id", patientUserIds)
          : Promise.resolve({ data: [] }),
      ]);

      if (!alive) return;

      const profile = Array.isArray(patientProfilesRes?.data) ? patientProfilesRes.data[0] || null : null;
      const ehrInfo = Array.isArray(ehrInfoRes?.data) ? ehrInfoRes.data[0] || null : null;
      setLiveClinicalProfile({
        ...(profile || {}),
        date_of_birth: ehrInfo?.date_of_birth || profile?.date_of_birth || null,
        primary_language: ehrInfo?.primary_language || profile?.primary_language || null,
        height_cm: ehrInfo?.height_cm ?? profile?.height_cm ?? null,
        weight_kg: ehrInfo?.weight_kg ?? profile?.weight_kg ?? null,
        blood_type: ehrInfo?.blood_type || profile?.blood_type || null,
        current_medications:
          ehrInfo?.current_medications ||
          profile?.current_medications ||
          profile?.medications ||
          null,
        allergies: ehrInfo?.allergies || profile?.allergies || null,
        chronic_conditions:
          ehrInfo?.chronic_conditions || profile?.chronic_conditions || profile?.medical_conditions || null,
        organ_donor:
          typeof ehrInfo?.is_organ_donor === "boolean"
            ? ehrInfo.is_organ_donor
            : profile?.organ_donor ?? profile?.is_organ_donor ?? null,
        is_organ_donor:
          typeof ehrInfo?.is_organ_donor === "boolean"
            ? ehrInfo.is_organ_donor
            : profile?.is_organ_donor ?? profile?.organ_donor ?? null,
      });
    } catch {
      if (alive) setLiveClinicalProfile(null);
    }
  }

  hydrateClinicalProfile();
  return () => {
    alive = false;
  };
}, [patientIdResolved, liveSnapshot]);

const patientFromState =
  liveSnapshot?.patient ||
  state?.patient ||
  restoredResolved?.patient ||
  restoredResolved?.patientProfile ||
  null;

React.useEffect(() => {
  let alive = true;

  async function hydrateEhrStatus() {
    const apptId = String(appointmentIdResolved || "").trim();
    const patientRef = String(patientIdResolved || "").trim();
    if (!apptId || !patientRef) {
      if (alive) setEhrAccessStatus("");
      return;
    }

    try {
      const { data: authData, error: authError } = await supabase.auth.getUser();
      const doctorAuthId = String(authData?.user?.id || "").trim();
      if (authError || !doctorAuthId) {
        if (alive) {
          setEhrAccessStatus("");
        }
        return;
      }

      const nextState = await loadAppointmentEhrAccessState({
        appointmentId: apptId,
        doctorId: doctorAuthId,
        doctorRef: doctorAuthId,
        patientUserId: patientRef,
        patientRef,
      });

      if (alive) {
        setEhrAccessStatus(ehrStatusFromServer(nextState?.request || nextState));
      }
    } catch {
      if (alive) {
        setEhrAccessStatus("");
      }
    }
  }

  hydrateEhrStatus();
  return () => {
    alive = false;
  };
}, [appointmentIdResolved, patientIdResolved]);

React.useEffect(() => {
  let alive = true;

  async function hydrateLatestVitals() {
    const status = ehrAccessStatus;
    if (status !== "granted") {
      if (alive) setLatestCanonicalVitals(null);
      return;
    }

    const patientRef = String(
      patientIdResolved ||
        liveSnapshot?.patient_row_id ||
        liveSnapshot?.patient?.patient_id ||
        liveSnapshot?.patient?.id ||
        ""
    ).trim();

    if (!patientRef) {
      if (alive) setLatestCanonicalVitals(null);
      return;
    }

    try {
      const patientIds = await resolvePatientRouteRefs(patientRef);
      const patientUserRows = patientIds.length
        ? await supabase.from("patients").select("id,user_id").in("id", patientIds)
        : { data: [] };
      const patientUserId = String(patientUserRows?.data?.[0]?.user_id || patientRef).trim();
      const latest = await fetchLatestPermittedPatientVitalSigns({
        patientId: patientUserId,
        appointmentId: appointmentIdResolved,
        accessStatus: status,
      });

      if (alive) setLatestCanonicalVitals(latest || null);
    } catch {
      if (alive) setLatestCanonicalVitals(null);
    }
  }

  hydrateLatestVitals();
  return () => {
    alive = false;
  };
}, [ehrAccessStatus, liveSnapshot, patientIdResolved]);

React.useEffect(() => {
  let alive = true;

  async function hydratePastAppointmentsCount() {
    const patientRef = String(patientIdResolved || "").trim();
    if (!patientRef) {
      if (alive) setPastAppointmentsCountLive(null);
      return;
    }

    try {
      const { data: authData, error: authError } = await supabase.auth.getUser();
      const doctorAuthId = String(authData?.user?.id || "").trim();
      if (authError || !doctorAuthId) {
        if (alive) setPastAppointmentsCountLive(null);
        return;
      }

      const patientIds = await resolvePatientRouteRefs(patientRef);
      if (patientIds.length === 0) {
        if (alive) setPastAppointmentsCountLive(0);
        return;
      }

      const { count, error } = await supabase
        .from("appointments")
        .select("id", { count: "exact", head: true })
        .eq("doctor_id", doctorAuthId)
        .in("patient_id", patientIds)
        .lt("appointment_date", new Date().toISOString())
        .neq("status", "cancelled");

      if (error) throw error;

      if (alive) {
        setPastAppointmentsCountLive(Number.isFinite(count) ? count : 0);
      }
    } catch {
      if (alive) setPastAppointmentsCountLive(null);
    }
  }

  hydratePastAppointmentsCount();
  window.addEventListener("afrimed:appointments:changed", hydratePastAppointmentsCount);
  return () => {
    alive = false;
    window.removeEventListener("afrimed:appointments:changed", hydratePastAppointmentsCount);
  };
}, [patientIdResolved]);

React.useEffect(() => {
  let alive = true;

  async function hydrateVitalRepDocuments() {
    const status = ehrAccessStatus;
    if (status !== "granted") {
      if (alive) setVitalRepClinicalDocuments([]);
      return;
    }

    const patientRef = String(patientIdResolved || "").trim();
    if (!patientRef) {
      if (alive) setVitalRepClinicalDocuments([]);
      return;
    }

    try {
      const { data: authData, error: authError } = await supabase.auth.getUser();
      const doctorAuthId = String(authData?.user?.id || "").trim();
      if (authError || !doctorAuthId) {
        if (alive) setVitalRepClinicalDocuments([]);
        return;
      }

      const patientIds = await resolvePatientRouteRefs(patientRef);
      if (patientIds.length === 0) {
        if (alive) setVitalRepClinicalDocuments([]);
        return;
      }

      const { data: appointments, error: appointmentsError } = await supabase
        .from("appointments")
        .select("id, appointment_date, status")
        .eq("doctor_id", doctorAuthId)
        .in("patient_id", patientIds)
        .neq("status", "cancelled")
        .order("appointment_date", { ascending: false })
        .limit(50);
      if (appointmentsError) throw appointmentsError;

      const appointmentRows = Array.isArray(appointments) ? appointments : [];
      const appointmentIds = appointmentRows.map((row) => String(row?.id || "").trim()).filter(Boolean);
      if (appointmentIds.length === 0) {
        if (alive) setVitalRepClinicalDocuments([]);
        return;
      }

      const appointmentById = new Map(
        appointmentRows.map((row) => [String(row?.id || "").trim(), row])
      );
      const { data: consultations, error: consultationsError } = await supabase
        .from("consultations")
        .select("id, appointment_id")
        .in("appointment_id", appointmentIds);
      if (consultationsError) throw consultationsError;

      const consultationRows = Array.isArray(consultations) ? consultations : [];
      const consultationIds = consultationRows.map((row) => String(row?.id || "").trim()).filter(Boolean);
      const appointmentIdByConsultationId = new Map(
        consultationRows.map((row) => [String(row?.id || "").trim(), String(row?.appointment_id || "").trim()])
      );
      if (consultationIds.length === 0) {
        if (alive) setVitalRepClinicalDocuments([]);
        return;
      }

      const { data: reports, error: reportsError } = await supabase
        .from("consultation_reports")
        .select("id, consultation_id, title, content, summary, created_at, updated_at")
        .in("consultation_id", consultationIds)
        .eq("report_type", "vitalrep_v0_1")
        .order("updated_at", { ascending: false });
      if (reportsError) throw reportsError;

      if (!alive) return;
      const nextDocuments = (Array.isArray(reports) ? reports : [])
        .filter((report) => String(report?.content?.status || "").trim().toLowerCase() === "finalized")
        .map((report) =>
          normalizeVitalRepEhrDocument(
            {
              ...report,
              consultation: {
                appointment_id: appointmentIdByConsultationId.get(String(report?.consultation_id || "").trim()),
              },
            },
            appointmentById
          )
        )
        .filter((document) => document.id);
      setVitalRepClinicalDocuments(nextDocuments);
    } catch {
      if (alive) setVitalRepClinicalDocuments([]);
    }
  }

  hydrateVitalRepDocuments();
  window.addEventListener("afrimed:vitalrep:changed", hydrateVitalRepDocuments);
  return () => {
    alive = false;
    window.removeEventListener("afrimed:vitalrep:changed", hydrateVitalRepDocuments);
  };
}, [ehrAccessStatus, patientIdResolved]);

    // ✅ Small helper: pick the first non-empty value
  const pick = (...vals) => {
    for (const v of vals) {
      if (v === 0) return v;
      if (v !== undefined && v !== null && String(v).trim() !== "") return v;
    }
    return null;
  };

// ✅ Define ONCE, early (prevents TDZ / "before initialization")
const patientResolved =
  patientFromState ||
  liveSnapshot?.patient ||
  restoredResolved?.patient ||
  restoredResolved?.patientProfile ||
  null;

const patientProfileResolved =
  liveClinicalProfile ||
  liveSnapshot?.patientProfile ||
  restoredResolved?.patientProfile ||
  patientResolved?.patientProfile ||
  null;

// --------------------------------------------------
// EHR SUMMARY SOURCE (from AppointmentDetails draft)
// + DEV fallback (so EHR screen is never blank)
// --------------------------------------------------
const patientName = pick(
  patientResolved?.full_name,
  patientResolved?.fullName,
  patientResolved?.name,
  restoredResolved?.patient_name,
  restoredResolved?.patientName,
  t("doctors.patientEhr.patientFallback", "Patient")
);

  const avatar = pick(
  patientResolved?.avatar_url,
  patientResolved?.avatarUrl,
  patientResolved?.avatar,
  restoredResolved?.avatar,
  restoredResolved?.patient_avatar,
  "/icons/circular-avatar-placeholder.svg"
);

const sex = pick(
  patientResolved?.sex,
  patientResolved?.gender,
  patientResolved?.biological_sex,
  restoredResolved?.sex,
  restoredResolved?.gender,
  null
);

// EHR preview can live in different shapes depending on the screen that saved the draft
const ehrSummary =
  liveSnapshot?.ehrSummary ||
  liveSnapshot?.patientProfile ||
  liveClinicalProfile ||
  restoredResolved?.ehrSummary ||
  restoredResolved?.ehr_summary ||
  restoredResolved?.ehrPreview ||
  restoredResolved?.ehr_preview ||
  restoredResolved?.ehr ||
  patientResolved?.ehrSummary ||
  patientResolved?.ehrPreview ||
  patientResolved?.ehr ||
  null;

// --------------------------------------------------
// MOCK_EHR (DEV fallback) — used by the pick(...) chain below
// --------------------------------------------------
const MOCK_EHR = {
  dob: "1994-08-26",
  primaryLanguage: "Tswana",

  // cm + kg (your markets)
  height_cm: 175,
  weight_kg: 72,

  bloodType: "O+",
  medications: "—",
  allergies: "Penicillin, Nuts",

  // keep keys matching what your pick() fallbacks are referencing
  chronicConditions: "Hypertension, Diabetes",
  organDonor: "Yes",

  pastAppointmentsCount: 4,
};

// Helpers to read from summary first, then patient, then restored, then DEV mock
const ehrPick = (...vals) => pick(...vals, DEV_BYPASS_EHR ? vals[vals.length - 1] : null);

// DOB (string or date)
const dob = pick(
  ehrSummary?.dob,
  ehrSummary?.date_of_birth,
  ehrSummary?.dateOfBirth,
  patientResolved?.dob,
  patientResolved?.date_of_birth,
  patientResolved?.dateOfBirth,
  restoredResolved?.dob,
  restoredResolved?.date_of_birth,
  DEV_BYPASS_EHR ? MOCK_EHR.dob : null
);

// Primary language
const primaryLanguage = pick(
  ehrSummary?.primary_language,
  ehrSummary?.primaryLanguage,
  ehrSummary?.languages,
  ehrSummary?.language,
  patientProfileResolved?.primary_language,
  patientProfileResolved?.languages,
  patientProfileResolved?.language,
  patientResolved?.primary_language,
  patientResolved?.languages,
  patientResolved?.language,
  restoredResolved?.patientProfile?.primary_language,
  restoredResolved?.patientProfile?.languages,
  restoredResolved?.patientProfile?.language,
  restoredResolved?.primaryLanguage,
  restoredResolved?.language,
  DEV_BYPASS_EHR ? MOCK_EHR.primaryLanguage : "—"
);
const primaryLanguageDisplay = formatLanguageDisplay(primaryLanguage, "—");

// Height/Weight (cm + kg)
const height = pick(
  ehrSummary?.height_cm,
  ehrSummary?.heightCm,
  ehrSummary?.height,
  patientProfileResolved?.height_cm,
  patientProfileResolved?.height,
  patientResolved?.height_cm,
  patientResolved?.height,
  restoredResolved?.patientProfile?.height_cm,
  restoredResolved?.patientProfile?.height,
  restoredResolved?.height_cm,
  restoredResolved?.height,
  DEV_BYPASS_EHR ? MOCK_EHR.height_cm : null
);

const weight = pick(
  ehrSummary?.weight_kg,
  ehrSummary?.weightKg,
  ehrSummary?.weight,
  patientProfileResolved?.weight_kg,
  patientProfileResolved?.weight,
  patientResolved?.weight_kg,
  patientResolved?.weight,
  restoredResolved?.patientProfile?.weight_kg,
  restoredResolved?.patientProfile?.weight,
  restoredResolved?.weight_kg,
  restoredResolved?.weight,
  DEV_BYPASS_EHR ? MOCK_EHR.weight_kg : null
);

const heightWeight =
  height && weight
    ? `${height} cm • ${weight} kg`
    : height
    ? `${height} cm`
    : weight
    ? `${weight} kg`
    : "—";
const canonicalVitalsDisplay = formatCanonicalVitals(latestCanonicalVitals);
const latestVitalsDisplay = canonicalVitalsDisplay.length
  ? canonicalVitalsDisplay
      .map(([key, value]) => {
        const labels = {
          bloodPressure: t("doctors.patientEhr.vitals.bloodPressure", "BP"),
          heartRate: t("doctors.patientEhr.vitals.heartRate", "Heart rate"),
          temperature: t("doctors.patientEhr.vitals.temperature", "Temperature"),
          oxygenSaturation: t("doctors.patientEhr.vitals.oxygenSaturation", "Oxygen"),
          respiratoryRate: t("doctors.patientEhr.vitals.respiratoryRate", "Resp. rate"),
          weight: t("doctors.patientEhr.vitals.weight", "Weight"),
          height: t("doctors.patientEhr.vitals.height", "Height"),
        };
        return `${labels[key] || key}: ${value}`;
      })
      .join("\n")
  : "—";

// Blood type
const bloodType = pick(
  ehrSummary?.blood_type,
  ehrSummary?.bloodType,
  patientProfileResolved?.blood_type,
  patientResolved?.blood_type,
  restoredResolved?.patientProfile?.blood_type,
  restoredResolved?.bloodType,
  restoredResolved?.blood_type,
  DEV_BYPASS_EHR ? MOCK_EHR.bloodType : "—"
);

// Medications
const medications = pick(
  ehrSummary?.medications,
  ehrSummary?.current_medications,
  ehrSummary?.currentMedications,
  patientProfileResolved?.current_medications,
  patientProfileResolved?.medications,
  patientResolved?.medications,
  patientResolved?.current_medications,
  restoredResolved?.patientProfile?.current_medications,
  restoredResolved?.patientProfile?.medications,
  restoredResolved?.medications,
  restoredResolved?.current_medications,
  DEV_BYPASS_EHR ? MOCK_EHR.medications : "—"
);

// Allergies
const allergies = pick(
  ehrSummary?.allergies,
  ehrSummary?.drug_allergies,
  ehrSummary?.drugAllergies,
  patientProfileResolved?.allergies,
  patientResolved?.allergies,
  restoredResolved?.patientProfile?.allergies,
  restoredResolved?.allergies,
  DEV_BYPASS_EHR ? MOCK_EHR.allergies : "—"
);

// Chronic conditions
const chronic = pick(
  ehrSummary?.chronic_conditions,
  ehrSummary?.chronicConditions,
  ehrSummary?.medical_conditions,
  ehrSummary?.conditions,
  patientProfileResolved?.chronic_conditions,
  patientProfileResolved?.medical_conditions,
  patientResolved?.chronic_conditions,
  patientResolved?.medical_conditions,
  restoredResolved?.patientProfile?.chronic_conditions,
  restoredResolved?.patientProfile?.medical_conditions,
  restoredResolved?.chronic_conditions,
  restoredResolved?.chronic,
  restoredResolved?.chronicConditions,
  DEV_BYPASS_EHR ? MOCK_EHR.chronicConditions : "—"
);
const chronicDisplay = formatConditionDisplay(chronic, "—");

// Organ donor
const donor = pick(
  ehrSummary?.organ_donor,
  ehrSummary?.organDonor,
  ehrSummary?.donor,
  patientProfileResolved?.organ_donor,
  patientProfileResolved?.is_organ_donor,
  patientResolved?.is_organ_donor,
  patientResolved?.organ_donor,
  restoredResolved?.patientProfile?.organ_donor,
  restoredResolved?.patientProfile?.is_organ_donor,
  restoredResolved?.organ_donor,
  restoredResolved?.donor,
  restoredResolved?.organDonor,
  DEV_BYPASS_EHR ? MOCK_EHR.organDonor : "—"
);

// Past appointments count (show 4 in DEV unless real data exists)
const pastAppointmentsCount = pick(
  pastAppointmentsCountLive,
  ehrSummary?.pastAppointmentsCount,
  restoredResolved?.pastAppointmentsCount,
  restoredResolved?.pastAppointments?.length,
  DEV_BYPASS_EHR ? MOCK_EHR.pastAppointmentsCount : null
);

 function handleBack() {
    const explicitReturnTo = String(location.state?.returnTo || "").trim();
    if (explicitReturnTo) {
      nav(explicitReturnTo, {
        replace: true,
        state: location.state?.returnState || restoredResolved || null,
      });
      return;
    }
    if (appointmentIdResolved) {
      nav(`/doctors/appointments/${appointmentIdResolved}`, {
        replace: true,
        state: restoredResolved || null,
      });
      return;
    }
    if (window.history.length > 1) nav(-1);
    else nav("/doctors/appointments");
  }

// Prefer explicit age from mock drafts; else derive from dob
const ageFromMock = pick(patientResolved?.age, patientResolved?.age_years, patientResolved?.ageYears, null);
const ageNumber = ageFromMock != null ? Number(ageFromMock) : null;

const derivedAge = ageNumber != null && !Number.isNaN(ageNumber) ? ageNumber : calcAge(dob);

const patientMeta = [
  derivedAge != null
    ? t("doctors.patientEhr.yearsOld", "{count} Years old", { count: derivedAge })
    : null,
  sex ? String(sex).charAt(0).toUpperCase() + String(sex).slice(1) : null,
]
  .filter(Boolean)
  .join(", ");

const patientVerificationStatus = String(
  pick(
    patientResolved?.id_verification_status,
    patientResolved?.verification_status,
    restoredResolved?.id_verification_status,
    restoredResolved?.verification_status,
    restoredResolved?.patientProfile?.id_verification_status,
    restoredResolved?.patientProfile?.verification_status,
    ""
  )
)
  .trim()
  .toLowerCase();

const patientVerificationIcon =
  patientVerificationStatus === "verified"
    ? ICONS.verified
    : ICONS.pendingVerification;

// --------------------------------------------------
// EHR ACCESS — DEV AUTHORITATIVE (SINGLE SOURCE)
// --------------------------------------------------

// DEV: always allow EHR so we can build and sign off
if (DEV_BYPASS_EHR === true) {
  // fall through to full EHR render below
} else {
  // NON-DEV: strict gate based on restored draft / DB later
  const ehrStatus = ehrAccessStatus;

  if (ehrStatus !== "granted") {
    const statusLabel =
      ehrStatus === "requested"
        ? t("doctors.patientEhr.accessRequested", "Access requested")
        : t("doctors.patientEhr.accessNotGranted", "Access not granted");

    return (
      <div className="min-h-[100dvh]" data-hide-tabbar="true">
        <div className="afrimed-rail afrimed-rail--comfortable pt-4 pb-24">
          <TopBarCenter
            title={t("doctors.patientEhr.title", "EHR")}
            onBack={handleBack}
          />

          <div className="mt-6 rounded-[12px] bg-[rgb(var(--clr-surface-cont-highest))] shadow-[0_1px_2px_rgba(15,23,42,0.06)] overflow-hidden">
            <div className="px-4 py-4 flex items-center justify-between gap-3">
              <div className="flex items-center gap-3 min-w-0">
                <img
                  src={ICONS.ehr}
                  className="h-6 w-6 opacity-80 shrink-0"
                />
                <div className="text-body-large text-on-surface">
                  {t("doctors.patientEhr.title", "EHR")}
                </div>
              </div>

              <div className="flex items-center gap-2 text-body-large text-on-surface-var">
                <span>{statusLabel}</span>
                <img
                  src={ICONS.accessRequested}
                  className="h-6 w-6 opacity-80"
                />
              </div>
            </div>

            <button
              type="button"
              onClick={() =>
                nav(
                  `/doctors/appointments/${appointmentIdResolved}/ehr-request`,
                  {
                    state: {
                      ...(restoredResolved || {}),
                      appointmentId: appointmentIdResolved,
                      patientId: patientIdResolved,
                    },
                  }
                )
              }
              className="w-full px-4 py-4 text-left"
            >
              <div className="text-body-large text-[rgb(var(--afrimed-primary))]">
                {t("doctors.patientEhr.goToRequest", "Go to request")}
              </div>
            </button>
          </div>
        </div>
      </div>
    );
  }
}

// --------------------------------------------------
// BELOW THIS LINE: FULL EHR SCREEN RENDERS
// --------------------------------------------------

  // --- GRANTED: render full EHR screen (blueprint) ---
  return (
    <div className="min-h-[100dvh]" data-hide-tabbar="true">
      <div className="afrimed-rail afrimed-rail--comfortable pt-4 pb-24">
        <TopBarCenter
            title={
                <div className="inline-flex items-center gap-2">
                <img src={ICONS.ehr} alt="" className="h-6 w-6" draggable="false" />
                <span>{t("doctors.patientEhr.title", "EHR")}</span>
                </div>
            }
            onBack={handleBack}
            />

        {/* Patient header */}
        <div className="mt-6 flex items-center gap-3 min-w-0">
          <div className="shrink-0">
            <AvatarThumb68
              src={avatar}
              alt={patientName}
              role="patient"
              shape="circle"
              style={{ width: 44, height: 44 }}
            />
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-2 min-w-0">
              <div className="text-body-large text-on-surface truncate">{patientName}</div>
              <img
                src={patientVerificationIcon}
                alt=""
                className="h-4 w-4 shrink-0"
                draggable="false"
              />
            </div>
            <div className="text-body-medium text-on-surface-var truncate">{patientMeta || "—"}</div>
          </div>
        </div>

        {/* Information */}
        <div className="mt-7">
          <div className="text-title-medium text-on-surface-var mb-3">
            {t("doctors.patientEhr.sections.information", "Information")}
          </div>

          <div className="rounded-[12px] bg-[rgb(var(--clr-surface-cont-highest))] shadow-[0_1px_2px_rgba(15,23,42,0.06)] overflow-hidden">
            <ListRow
              rounded={false}
              className="bg-transparent"
              leading={ICONS.dob}
              title={t("doctors.patientEhr.fields.dob", "Date of birth")}
              support={
                dob
                  ? new Intl.DateTimeFormat(languageCode, {
                      year: "numeric",
                      month: "long",
                      day: "numeric",
                    }).format(new Date(dob))
                  : "—"
              }
            />
            <ListRow
              rounded={false}
              className="bg-transparent"
              leading={ICONS.language}
              title={t("doctors.patientEhr.fields.language", "Primary Language")}
              support={primaryLanguageDisplay}
            />
            <ListRow
              rounded={false}
              className="bg-transparent"
              leading={ICONS.heightWeight}
              title={t("doctors.patientEhr.fields.heightWeight", "Height & Weight")}
              support={heightWeight || "—"} // keep your cm/kg logic
            />
            <ListRow
              rounded={false}
              className="bg-transparent"
              leading={ICONS.heightWeight}
              title={t("doctors.patientEhr.fields.latestVitals", "Latest vitals")}
              support={latestVitalsDisplay}
            />
            <ListRow
              rounded={false}
              className="bg-transparent"
              leading={ICONS.bloodtype}
              title={t("doctors.patientEhr.fields.bloodType", "Blood type")}
              support={bloodType || "—"}
            />
            <ListRow
              rounded={false}
              className="bg-transparent"
              divider={false}
              leading={ICONS.medications}
              title={t("doctors.patientEhr.fields.medications", "Medications")}
              support={medications || "—"}
            />
          </div>
        </div>

        {/* Conditions */}
        <div className="mt-7">
          <div className="text-title-medium text-on-surface-var mb-3">
            {t("doctors.patientEhr.sections.conditions", "Conditions")}
          </div>

          <div className="rounded-[12px] bg-[rgb(var(--clr-surface-cont-highest))] shadow-[0_1px_2px_rgba(15,23,42,0.06)] overflow-hidden">
            <ListRow
              rounded={false}
              className="bg-transparent"
              leading={ICONS.allergy}
              title={t("doctors.patientEhr.fields.allergies", "Allergies")}
              support={allergies || "—"}
            />
            <ListRow
              rounded={false}
              className="bg-transparent"
              leading={ICONS.conditions}
              title={t("doctors.patientEhr.fields.chronic", "Chronic Conditions")}
              support={chronicDisplay}
            />
            <ListRow
              rounded={false}
              className="bg-transparent"
              divider={false}
              leading={ICONS.organDonor}
              title={t("doctors.patientEhr.fields.organDonor", "Organ Donor")}
              support={
                donor === true || donor === "true"
                  ? t("common.yes", "Yes")
                  : donor === false || donor === "false"
                  ? t("common.no", "No")
                  : donor || "—"
              }
            />
          </div>
        </div>

        {/* Record */}
        <div className="mt-7">
          <div className="text-title-medium text-on-surface-var mb-3">
            {t("doctors.patientEhr.sections.record", "Record")}
          </div>

          <div className="rounded-[12px] bg-[rgb(var(--clr-surface-cont-highest))] shadow-[0_1px_2px_rgba(15,23,42,0.06)] overflow-hidden">
            <ListRow
            rounded={false}
            className="bg-transparent"
            divider={false}
            leading={ICONS.pastAppointments}
            title={t("doctors.patientEhr.fields.pastAppointments", "Past Appointments")}
            support=""
            showChevron
            trailing={
                <span className="text-body-large text-on-surface-var mr-2">
                {pastAppointmentsCount ?? "—"}
                </span>
            }
            onClick={() => {
                const routePatientId = String(
                  patientIdResolved || patientFromState?.id || patientIdParam || ""
                ).trim();

                if (!routePatientId) {
                  return nav("/doctors/appointments", {
                    state: {
                      from: "doctor-ehr",
                      appointmentId: appointmentIdResolved || null,
                    },
                  });
                }

                nav(`/doctors/patients/${encodeURIComponent(routePatientId)}/ehr/past-appointments`, {
                state: {
                    from: "doctor",
                    patientId: routePatientId,
                    patient: patientFromState || null,
                    restored: restoredResolved || null,
                    appointmentId: appointmentIdResolved || null,
                  },
                });
              }}
            />
          </div>
        </div>

        {vitalRepClinicalDocuments.length ? (
          <div className="mt-7">
            <div className="text-title-medium text-on-surface-var mb-3">
              {t("doctors.patientEhr.sections.clinicalDocuments", "Clinical documents")}
            </div>

            <div className="space-y-3">
              {vitalRepClinicalDocuments.map((document) => (
                <div
                  key={document.id}
                  className="rounded-[12px] bg-[rgb(var(--clr-surface-cont-highest))] px-4 py-3 shadow-[0_1px_2px_rgba(15,23,42,0.06)]"
                >
                  <div className="flex flex-wrap items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="text-body-large text-on-surface">
                        {t("doctors.patientEhr.vitalRep.title", "VitalRep™ consultation note")}
                      </div>
                      <div className="mt-0.5 text-body-medium text-on-surface-var">
                        {formatEhrDateTime(document.finalizedAt || document.appointmentDate)}
                      </div>
                    </div>
                    {document.amendedAt ? (
                      <span className="rounded-full bg-[rgba(0,189,196,0.12)] px-3 py-1 text-label-small text-[#008A8F]">
                        {t("doctors.patientEhr.vitalRep.amended", "Amended")}
                      </span>
                    ) : null}
                  </div>
                  <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
                    {[
                      ["Subjective", document.subjective],
                      ["Objective", document.objective],
                      ["Assessment", document.assessment],
                      ["Plan", document.plan],
                    ].map(([label, value]) => (
                      <div
                        key={label}
                        className="min-w-0"
                      >
                        <div className="text-label-medium text-on-surface-var">{label}</div>
                        <div className="mt-1 whitespace-pre-wrap text-body-medium leading-5 text-on-surface">
                          {value || "—"}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}
